import type {
  MarketPoolQuoteSnapshot,
  OutcomePoolQuoteContext,
  OutcomePoolQuoteSnapshot,
} from '@morfi/market-math';

/** Hydrates the JSON-safe chain snapshot into the canonical executable quote context. */
export function outcomePoolQuoteContextFromSnapshot(
  snapshot: MarketPoolQuoteSnapshot,
  outcomeIndex: number,
  stateStale: boolean = false,
): OutcomePoolQuoteContext {
  const chainClock = {
    slot: BigInt(snapshot.chainSlot),
    unixTimestampSeconds: BigInt(snapshot.chainUnixTimestampSeconds),
  };
  const outcome = snapshot.outcomes.find((candidate) => candidate.outcomeIndex === outcomeIndex);
  const venue = {
    lifecycle: snapshot.lifecycle,
    outcomeIndex,
    winningOutcomeIndex: snapshot.winningOutcomeIndex,
    bondingCurveClosed:
      snapshot.bondingCurvesClosed === true || (outcome?.bondingCurve?.closedForTrade ?? false),
    hasCpSwapPool: outcome?.cpSwap !== null && outcome?.cpSwap !== undefined,
  } as const;

  if (stateStale) return { chainClock, poolModel: 'stale', venue };
  if (outcome === undefined) return { chainClock, poolModel: 'missing', venue };
  if (snapshot.lifecycle === 'trading') {
    return bondingCurveContext(snapshot, outcome, venue, chainClock);
  }
  if (snapshot.lifecycle === 'graduated' && outcome.cpSwap !== null) {
    return {
      chainClock,
      poolModel: 'cp_swap',
      venue,
      cpSwap: {
        quoteReserveD6: BigInt(outcome.cpSwap.quoteReserveD6),
        outcomeReserveD6: BigInt(outcome.cpSwap.outcomeReserveD6),
        tradeFeeRateMillionths: BigInt(outcome.cpSwap.tradeFeeRateMillionths),
        creatorFeeRateMillionths: BigInt(outcome.cpSwap.creatorFeeRateMillionths),
        creatorFeeEnabled: outcome.cpSwap.creatorFeeEnabled,
        creatorFeeOn: outcome.cpSwap.creatorFeeOn,
        outcomeTokenIndex: outcome.cpSwap.outcomeTokenIndex,
        swapEnabled: outcome.cpSwap.swapEnabled,
        openTimeUnixSeconds: BigInt(outcome.cpSwap.openTimeUnixSeconds),
      },
    };
  }
  return { chainClock, poolModel: 'missing', venue };
}

function bondingCurveContext(
  snapshot: MarketPoolQuoteSnapshot,
  outcome: OutcomePoolQuoteSnapshot,
  venue: OutcomePoolQuoteContext['venue'],
  chainClock: OutcomePoolQuoteContext['chainClock'],
): OutcomePoolQuoteContext {
  const curve = outcome.bondingCurve;
  const gate = snapshot.bondingCurveTradeGate;
  if (
    curve === null ||
    gate === null ||
    snapshot.buyFeeBps === null ||
    snapshot.sellBaseFeeBps === null ||
    snapshot.sellPenaltyFeeBps === null ||
    snapshot.prizePoolFeeBps === null ||
    snapshot.minimumQuoteTradeD6 === null
  ) {
    return { chainClock, poolModel: 'missing', venue };
  }
  const realQuoteReservesD6: bigint[] = [];
  for (const candidate of snapshot.outcomes) {
    if (candidate.realQuoteReserveD6 === null) {
      return { chainClock, poolModel: 'missing', venue };
    }
    realQuoteReservesD6.push(BigInt(candidate.realQuoteReserveD6));
  }
  return {
    chainClock,
    poolModel: 'bonding_curve',
    venue,
    bondingCurve: {
      state: {
        virtualQuoteReserveD6: BigInt(curve.virtualQuoteReserveD6),
        poolQuoteReserveD6: BigInt(curve.poolQuoteReserveD6),
        initialOutcomeSupplyD6: BigInt(curve.initialOutcomeSupplyD6),
        poolOutcomeReserveD6: BigInt(curve.poolOutcomeReserveD6),
      },
      fees: {
        buyFeeBps: BigInt(snapshot.buyFeeBps),
        sellBaseFeeBps: BigInt(snapshot.sellBaseFeeBps),
        sellPenaltyFeeBps: BigInt(snapshot.sellPenaltyFeeBps),
        prizePoolFeeBps: BigInt(snapshot.prizePoolFeeBps),
        minimumQuoteTradeD6: BigInt(snapshot.minimumQuoteTradeD6),
      },
      convictionReferenceMarginPpb: BigInt(gate.convictionReferenceMarginPpb),
      convictionOutcomeIndices: snapshot.outcomes.map((candidate) => candidate.outcomeIndex),
      conviction: {
        realQuoteReservesD6,
        outcomeConvictionPpb: gate.outcomeConvictionPpb.map((value) => BigInt(value)),
        lastConvictionUpdateUnixSeconds: BigInt(gate.lastConvictionUpdateUnixSeconds),
        convictionBaselineReserveQuoteD6: BigInt(gate.convictionBaselineReserveQuoteD6),
        convictionRetentionPerSecondPpb: BigInt(gate.convictionRetentionPerSecondPpb),
        convictionResolutionThresholdPpb: BigInt(gate.convictionResolutionThresholdPpb),
        resolutionAtUnixSeconds:
          gate.resolutionAtUnixSeconds == null
            ? gate.resolutionAtUnixSeconds
            : BigInt(gate.resolutionAtUnixSeconds),
        timedTradingWindow:
          gate.timedTradingWindow === undefined
            ? undefined
            : {
                startUnixSeconds: BigInt(gate.timedTradingWindow.startUnixSeconds),
                endUnixSeconds: BigInt(gate.timedTradingWindow.endUnixSeconds),
              },
      },
    },
  };
}
